import * as _ from 'lodash';
import { useSelector } from 'react-redux';

import { SNIPPET_DETECTION } from '../../../../../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../../../../../shared/fetchers';
import Locator from '../../../../../../../../shared/Locator';
import { isFreeTier } from '../../../../../../../../shared/subscriptions';
import { useParams } from '../../../../../../../contexts/RouterContext';
import { window } from '../../../../../../../globals';
import { useReleaseGroupAllProjects } from '../../../../../../../hooks/api/releaseGroups';
import useIsLicenseConcludedEnabled from '../../../../../../../hooks/features/useIsLicenseConcludedEnabled';
import * as reportGeneration from '../../../../../../../redux/reportGeneration';
import { FeatureFlagRequired } from '../../../../../../generic';
import FilterCardCheckboxes from '../../../../../../generic/Filter/FilterCardCheckboxes';
import FilterCardNestedCheckboxes from '../../../../../../generic/Filter/FilterCardNestedCheckboxes';
import PaidFeatureGate from '../../../../../../generic/Gates/PaidFeatureGate';
import { ReportOptionDetails } from '../../../utils';
import { licenseIncludeOptions, reportTypeFormatMap } from '../../constants';
import type { SelectOption, SelectOptionWithAttributes, SetState } from '../../../../../../../types';
import type { ReportCategory } from '../../../../shared/types';
import type { FormatSelection } from '../FormatSelect';

import styles from './Filter.module.less';

/**
 * Mini hook determining whether the Support Status option should be shown based on
 * the project(s) fetcher type(s).
 */
const useIsBinaryScanProject = () => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const params = useParams<{ projectGroupId: number | null, project_id: string | null }>();
  // This component is used for both project and release groups;
  // But we cannot call useReleaseGroupAllProjects non-deterministically
  const allProjectLocators = useReleaseGroupAllProjects(params.projectGroupId ?? 0).data ?? [];

  if (params.projectGroupId) { // 0 is not a valid projectGroupId anyway
    // release group case
    return allProjectLocators.every(loc => {
      const { fetcher } = Locator.parse(loc);
      return fetcher === BinaryFetcherName;
    });
  }

  if (params.project_id) {
    // single project case
    const { fetcher } = Locator.parse(params.project_id);
    return fetcher === BinaryFetcherName;
  }

  return false;
};

type Props = {
  format: FormatSelection;
  category: ReportCategory;
  values: SelectOption[];
  setValues: SetState<SelectOption[]>;
  licenseValues: SelectOption[];
  setLicenseValues: SetState<SelectOption[]>;
};

const IncludeCheckboxGroup = ({
  format,
  category,
  values,
  setValues,
  licenseValues,
  setLicenseValues,
}: Props) => {
  const isBinaryProject = useIsBinaryScanProject();
  const isCopyrightListSelected = licenseValues.find(v => v.value === 'includeCopyrightList');
  const isPaidFeatureGateRestricted = isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  });
  const isConcludedLicenseEnabled = useIsLicenseConcludedEnabled(window?.user?.organizationId ?? 0);
  const projectType = useSelector(reportGeneration.selectors.getProjectType);
  const [licenseOptions, flatOptions] = _.partition(
    Object.values(ReportOptionDetails)
      .filter(({ key, format: applicableFormats, category: applicableCategories }) => {
        // Filter out includeConcludedLicenses option if the feature flag is not enabled
        if (key === 'includeConcludedLicenses' && !isConcludedLicenseEnabled) {
          return false;
        }

        // Filter out includeFOSSADependencies option when project type is not release group
        if (key === 'includeFOSSADependencies' && projectType !== 'releaseGroup') {
          return false;
        }

        // Category and format applicability
        const isCorrectCategory = applicableCategories.includes(category);
        const isApplicableFormat = applicableFormats.has(reportTypeFormatMap[format.value]);
        return isCorrectCategory && isApplicableFormat;
      })
      .map(({ key: value, title, description }) => {
        const isProjectDeclaredLicense = title === 'Project Declared License';
        const isUnlicensedCopyrights = title === 'Copyrights from non-license text';
        const isSupportStatus = title === 'Support Status';
        const label = isProjectDeclaredLicense ? 'Declared Licenses' : title;
        const isNestedOption = licenseIncludeOptions.has(value) || isProjectDeclaredLicense;
        const isDisabled = (
          (isBinaryProject && isSupportStatus) ||
          (isUnlicensedCopyrights && !isCopyrightListSelected)
        );
        const tooltip = (
          <div className={styles.tooltipText}>
            {description}
          </div>
        );
        return {
          isNestedOption,
          label,
          value,
          isDisabled,
          tooltip,
        };
      }),
    ({ isNestedOption }) => isNestedOption,
  );

  const setChecked = (options: SelectOptionWithAttributes[]) => {
    const copyrights = options.find(o => o.value === 'includeCopyrightList');
    if (!copyrights) {
      return setLicenseValues(options.filter(o => o.value !== 'includeNonLicenseCopyrights'));
    }
    return setLicenseValues(options);
  };

  return (
    <div className="width-full" data-test-id="sbom-filters-include">
      <FilterCardCheckboxes
        label="Included"
        options={flatOptions}
        values={values}
        onChange={setValues}
        tooltipPlacement="left"
        renderOption={(option, checkbox) => {
          // Everything except the transitive dependencies option is a paid feature
          if (option.value === 'includeDeepDependencies') {
            return checkbox;
          }

          if (option.value === 'includeSnippetDependencies') {
            return <FeatureFlagRequired flagName={SNIPPET_DETECTION}>{checkbox}</FeatureFlagRequired>;
          }
          return (
            <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
              {checkbox}
            </PaidFeatureGate>
          );
        }}
      />
      <PaidFeatureGate isRestricted={isPaidFeatureGateRestricted} hidePill>
        <FilterCardNestedCheckboxes
          options={licenseOptions}
          values={licenseValues}
          onChange={setChecked}
          parentCheckboxLabel="List of licenses"
          parentTooltip={
            <div className={styles.tooltipText}>
              A complete collection of all licenses detected across your project&apos;s dependencies.
            </div>
          }
          tooltipPlacement="left"
        />
      </PaidFeatureGate>
    </div>
  );
};

export default IncludeCheckboxGroup;
